import { REGEX } from "@common/constants/regex.constant";

// function to get the last date in the quarter from a string of YYYY-Q1 format
export const getLastDateInQuarter = (quarter: string, isUTC = true): Date => {
    const yearNumber = Number.parseInt(quarter.slice(0, 4));
    const quarterNumber = Number.parseInt(quarter.slice(6));
    const day = [31, 30, 30, 31][quarterNumber - 1];
    const month = quarterNumber * 3;
    let date: Date;
    if (isUTC) {
        date = new Date(Date.UTC(yearNumber, month-1, day));
    } else {
        date = new Date(yearNumber, month-1, day);
    }
    return date;
}

// function to convert string date to Date object when parsing JSON
export const DateTimeReviver = (key: any, value: any): any => {
  // Match full ISO datetime or date-only
  if (typeof value === 'string' && REGEX.ISO_DATE.test(value)) {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return value;
};


export const getQuarter = (date: Date): string => {
    const month = date.getMonth();
    const quarter = Math.floor(month / 3) + 1;
    return `${date.getFullYear()}-Q${quarter}`;
}

//Ignore timezone conversion and time from the input date, get date only
export const convertUTCLocalOffset = (date:Date):Date =>{
    let convertOffsetDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));;
    return convertOffsetDate;
}

/**
 * Converts a UTC-midnight timestamp/string to a local Date whose local calendar
 * date matches the UTC date.  Use when populating a date picker so the displayed
 * date is not shifted by the browser timezone offset.
 * e.g. "2022-11-16T00:00:00.000Z" in UTC-6 → local Nov 16 (not Nov 15)
 */
export const utcToLocalDate = (value: string | number | Date): Date => {
    const parsed = value instanceof Date ? value : new Date(value);
    return new Date(parsed.getTime() + parsed.getTimezoneOffset() * 60000);
};

/**
 * Reverses utcToLocalDate: converts a local-midnight Date back to a UTC-midnight
 * timestamp so the value stored/emitted always represents the same calendar date in UTC.
 */
export const localDateToUtcTimestamp = (date: Date): number => {
    return date.getTime() - date.getTimezoneOffset() * 60000;
};

export function getQuarterEndDates(startDate: Date, endDate: Date): Date[] {
    const quarterEndMonths = [2, 5, 8, 11]; // March, June, September, December
    let current = new Date(startDate);
    current.setHours(0, 0, 0, 0); // Ensure no time part affects the comparison
    const end = new Date(endDate.valueOf()+1000*60*60*48);
    end.setHours(23, 59, 59, 999); // Ensure end date is inclusive
    const quarterEndDates: Date[] = [];

    while (current <= end) {
        const year = current.getFullYear();

        for (let month of quarterEndMonths) {
            const quarterEnd = new Date(year, month + 1, 0); // Last day of the quarter month
            if (quarterEnd >= startDate && quarterEnd <= endDate) {
                quarterEndDates.push(quarterEnd);
            }
        }

        // Move to the next year
        current = new Date(year + 1, 0, 1);
    }

    return quarterEndDates;
}

export function dateToString(date: Date): string {
    return date.toISOString().split('T')[0];
}

export function getPreviousQuarter(quarter: string):string {
    const [year,qtr] = quarter.split('-');
    let previousQuarter;
    switch (qtr) {
      case 'Q1':
        previousQuarter = parseInt(year)-1 + '-Q4';
        break;
      case 'Q2':
        previousQuarter = year + '-Q1';
        break;
      case 'Q3':
        previousQuarter = year + '-Q2';
        break;
      case 'Q4':
        previousQuarter = year + '-Q3';
        break;
    }

    return previousQuarter;
}

export function getNextQuarter(quarterStr: string): string {
    const match = quarterStr.match(REGEX.YEAR_QUARTER_Q);
    if (!match) {
      throw new Error("Invalid quarter format. Expected format: YYYY-Q1 to YYYY-Q4");
    }
  
    let year = parseInt(match[1], 10);
    let quarter = parseInt(match[2], 10);
  
    if (quarter === 4) {
      year += 1;
      quarter = 1;
    } else {
      quarter += 1;
    }
  
    return `${year}-Q${quarter}`;
  }

  export const getFirstDateInQuarter = (quarter: string, isUTC = true): Date => {
    const year = Number.parseInt(quarter.slice(0, 4));
    const quarterNumber = Number.parseInt(quarter.slice(6));

    // First month of the quarter: Q1 => Jan (0), Q2 => Apr (3), etc.
    const firstMonthIndex = (quarterNumber - 1) * 3;

    let date: Date;
    if (isUTC) {
        date = new Date(Date.UTC(year, firstMonthIndex, 1));
    } else {
        date = new Date(year, firstMonthIndex, 1);
    }

    return date;
};

export function sortQuarters(quarters: string[]): string[] {
    return quarters.sort((a, b) => {
        const [yearA, quarterA] = a.split('-Q').map(Number);
        const [yearB, quarterB] = b.split('-Q').map(Number);
        
        if (yearA !== yearB) {
            return yearB - yearA; // Sort by year in descending order
        }
        return quarterB - quarterA; // Then by quarter in descending order
    });
}

export function getNoOfDays(startDate: string | Date, endDate: string | Date): number {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diffTime = end.getTime() - start.getTime();
  return diffTime / (1000 * 60 * 60 * 24) + 1; 
}
